import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, Send, ChevronLeft, CheckCircle2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAdmin } from "@/contexts/AdminContext";

/**
 * /admin/digest — Manual triggers for the daily digest email.
 *
 * - "Send preview to me" → /api/digest/preview (any signed-in user).
 * - "Send to everyone now" → /api/admin/cron/digest (admin only).
 *
 * The send-to-everyone endpoint is also designed to be hit by an
 * external cron once per day.
 */
export default function AdminDigest() {
  const { unlocked } = useAdmin();
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const run = async (kind) => {
    setBusy(kind); setErr(""); setResult(null);
    try {
      const url = kind === "preview" ? "/digest/preview" : "/admin/cron/digest";
      const { data } = await api.post(url);
      setResult({ kind, ...data });
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl flex flex-col gap-6">
        <Link to="/admin" className="text-sm flex items-center gap-1"
              style={{ color: "var(--text-dim)" }} data-testid="back-to-admin">
          <ChevronLeft size={14} /> Back Office
        </Link>

        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>
            Email
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold" style={{ letterSpacing: "-0.03em" }}>
            Daily Digest
          </h1>
          <p style={{ color: "var(--text-dim)" }} className="mt-2">
            Born Day rolls every user&apos;s pending alerts into a single email each day.
            You can fire it manually here, or wire a cron to <code className="bd-mono">POST /api/admin/cron/digest</code>.
          </p>
        </div>

        <div className="bd-card flex flex-col gap-3" style={{ padding: "1.2rem" }}>
          <div className="flex items-center gap-2">
            <Mail size={18} />
            <div className="font-semibold">Send a preview to myself</div>
          </div>
          <p className="text-sm" style={{ color: "var(--text-dim)" }}>
            Generates a digest using <b>your</b> pending notifications and sends
            it only to your email. Best for verifying the template.
          </p>
          <button
            className="bd-btn"
            style={{ alignSelf: "flex-start" }}
            onClick={() => run("preview")}
            disabled={busy !== ""}
            data-testid="send-preview-btn"
          >
            {busy === "preview" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            <span style={{ marginLeft: 6 }}>Send preview to me</span>
          </button>
        </div>

        <div className="bd-card flex flex-col gap-3" style={{ padding: "1.2rem" }}>
          <div className="flex items-center gap-2">
            <Mail size={18} />
            <div className="font-semibold">Send to every active user now</div>
          </div>
          <p className="text-sm" style={{ color: "var(--text-dim)" }}>
            Fans the digest out to every user with at least one pending alert in
            the last 26 hours. Skips users who opted out.
            {!unlocked && (
              <span style={{ color: "#ff3b30" }}> Back Office must be unlocked.</span>
            )}
          </p>
          <button
            className="bd-btn"
            style={{ alignSelf: "flex-start", background: "var(--primary)", color: "var(--primary-fg)" }}
            onClick={() => run("all")}
            disabled={busy !== "" || !unlocked}
            data-testid="send-all-btn"
          >
            {busy === "all" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            <span style={{ marginLeft: 6 }}>Send to everyone</span>
          </button>
        </div>

        {result && (
          <div className="bd-card flex items-start gap-3" data-testid="result-card"
               style={{ padding: "1rem 1.2rem", borderColor: "var(--primary)" }}>
            <CheckCircle2 size={18} style={{ color: "var(--primary)" }} />
            <div className="text-sm">
              <div className="font-semibold">Run complete</div>
              <div style={{ color: "var(--text-dim)" }}>
                Sent: <b>{result.sent ?? 0}</b> · Skipped: <b>{result.skipped ?? 0}</b>
                {result.error && <> · Error: <code className="bd-mono">{result.error}</code></>}
              </div>
              {result.errors && result.errors.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary>Show {result.errors.length} delivery error(s)</summary>
                  <ul>
                    {result.errors.map((e, i) => (
                      // Errors are short, distinct strings from Resend; the
                      // string itself is a stable key. We append the index
                      // to disambiguate the rare case of two identical
                      // error strings in the same run.
                      <li key={`${e}-${i}`}><code>{e}</code></li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>
        )}

        {err && (
          <div className="bd-card text-sm" style={{ padding: "0.8rem 1rem", color: "#ff3b30" }}>
            {err}
          </div>
        )}

        <p className="text-xs" style={{ color: "var(--text-dim)" }}>
          Sandbox mode: emails are sent from <code className="bd-mono">onboarding@resend.dev</code> and
          only deliver to <b>email addresses verified on your Resend account</b>.
          Verify <code className="bd-mono">bornday.me</code> in Resend → Domains to send to anyone.
        </p>
      </div>
    </AppShell>
  );
}
